import { useContext, useEffect, useMemo, useState } from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  BoxIcon,
  Building2,
  DollarSignIcon,
  LetterTextIcon,
  MapPinnedIcon,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useToast } from 'hooks/useToaster';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import {
  Load,
  LoadStopTypes,
  NormalizedLoad,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
} from 'utils/formValidation';

import { TMS } from '../../../types/enums/Integrations';
import AdditionalReferencesForm, {
  normalizeQuantumEdgeAdditionalReferences,
} from './QuantumEdgeSectionForms/AdditionalReferences';
import CustomerSectionForm from './QuantumEdgeSectionForms/Customer';
import { QuantumEdgeRatesForm } from './QuantumEdgeSectionForms/Rates';
import SpecificationsSectionForm from './QuantumEdgeSectionForms/Specifications';
import StopForm from './QuantumEdgeSectionForms/Stop';
import { QuantumEdgeStopsTimeline } from './QuantumEdgeSectionForms/components/QuantumEdgeStopsTimeline';
import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from './utils/stopValidation';

enum AvailableTabs {
  customer = 'customer',
  rates = 'rates',
  additionalReferences = 'additionalReferences',
  specifications = 'specifications',
  pickup = 'pickup',
  consignee = 'consignee',
  route = 'route',
}

type LoadBuildingTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & { name: FieldPath<NormalizedLoad> };

export const LoadBuildingTextInput = (props: LoadBuildingTextInputProps) => (
  <RHFTextInput {...props} />
);

const withQuantumEdgeAdditionalReferences = (
  load: NormalizedLoad
): NormalizedLoad => ({
  ...load,
  additionalReferences: normalizeQuantumEdgeAdditionalReferences(
    load.additionalReferences ?? []
  ),
});

const initQuantumEdgeLoadBuildingForm = (tmsName: string): NormalizedLoad =>
  withQuantumEdgeAdditionalReferences(
    normalizeLoad(tmsName, {
      ...createInitLoad(),
      mode: 'OTR',
      specifications: {
        ...createInitSpecs(),
        transportType: "53VR - Van or Reefer - 53'",
      },
      rateData: {
        ...createInitRateData(),
        customerRateType: 'Flat Rate',
        carrierRateType: 'Flat Rate',
      },
    })
  );

interface QuantumEdgeLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void | Promise<void>;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

export default function QuantumEdgeLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onSuccess: _onSuccess,
  onError: _onError,
}: QuantumEdgeLoadFormProps) {
  const tmsName = TMS.QuantumEdge;
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTabs, setActiveTabs] = useState<string[]>(
    Object.values(AvailableTabs)
  );

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    if (!isCreateMode && load) {
      return withQuantumEdgeAdditionalReferences(normalizeLoad(tmsName, load));
    }

    if (isCreateMode) {
      return initQuantumEdgeLoadBuildingForm(tmsName);
    }

    return initQuantumEdgeLoadBuildingForm(tmsName);
  }, [tmsName, isCreateMode, load]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  const { handleSubmit } = formMethods;

  useEffect(() => {
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });
  }, [memoizedDefaultValues]);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  const handleInvalid: SubmitErrorHandler<NormalizedLoad> = async (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.QuantumEdge,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });
  };

  const handleFormSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.QuantumEdge,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      data.pickup?.externalTMSID,
      data.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      data.pickup?.readyTime,
      data.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    if (onSubmit) {
      setIsLoading(true);
      try {
        await onSubmit(data);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const buttonText = isCreateMode ? ButtonText.BuildLoad : ButtonText.UpdateTMS;
  const buttonNamePosthog = isCreateMode
    ? ButtonNamePosthog.BuildLoad
    : ButtonNamePosthog.UpdateTMS;

  return (
    <Flex direction='col' className='mb-5 w-full'>
      <ExtendedFormProvider
        aiDefaultValues={false}
        aiIconOnly={true}
        highlightDirtyFields={!isCreateMode}
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={handleSubmit(handleFormSubmit, handleInvalid)}
            className='w-full'
          >
            <Accordion
              type='multiple'
              value={activeTabs}
              onValueChange={setActiveTabs}
            >
              <LoadSectionAccordionItem
                label='Customer'
                icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.customer}
                activeTabs={activeTabs}
              >
                <CustomerSectionForm
                  formMethods={formMethods}
                  originalSuggestionData={memoizedDefaultValues}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='References'
                icon={<LetterTextIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.additionalReferences}
                activeTabs={activeTabs}
              >
                <AdditionalReferencesForm
                  formMethods={formMethods}
                  originalSuggestionData={memoizedDefaultValues}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Specifications'
                icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.specifications}
                activeTabs={activeTabs}
              >
                <SpecificationsSectionForm formMethods={formMethods} />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Rates'
                icon={<DollarSignIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.rates}
                activeTabs={activeTabs}
              >
                <QuantumEdgeRatesForm
                  formMethods={formMethods}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              {!isCreateMode &&
              load?.moreThanTwoStops &&
              load?.stops?.length ? (
                <>
                  <LoadSectionAccordionItem
                    label='Route'
                    icon={<MapPinnedIcon className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.route}
                    activeTabs={activeTabs}
                  >
                    <QuantumEdgeStopsTimeline
                      formMethods={formMethods}
                      originalSuggestionData={memoizedDefaultValues}
                    />
                  </LoadSectionAccordionItem>
                </>
              ) : (
                <>
                  <LoadSectionAccordionItem
                    label='Pickup'
                    icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.pickup}
                    activeTabs={activeTabs}
                  >
                    <StopForm
                      stop={LoadStopTypes.Pickup}
                      formMethods={formMethods}
                      isCreateMode={isCreateMode}
                      autoExpandAccordions={true}
                      originalSuggestionData={memoizedDefaultValues}
                    />
                  </LoadSectionAccordionItem>

                  <LoadSectionAccordionItem
                    label='Consignee'
                    icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.consignee}
                    activeTabs={activeTabs}
                  >
                    <StopForm
                      stop={LoadStopTypes.Consignee}
                      formMethods={formMethods}
                      isCreateMode={isCreateMode}
                      autoExpandAccordions={true}
                      originalSuggestionData={memoizedDefaultValues}
                    />
                  </LoadSectionAccordionItem>
                </>
              )}
            </Accordion>

            <Flex direction='col' className='w-full mt-4'>
              <Button
                buttonNamePosthog={buttonNamePosthog}
                type='submit'
                className='w-full'
                disabled={isLoading}
                data-testid={
                  isCreateMode
                    ? 'create-load-submit'
                    : 'load-info-update-submit'
                }
                logProperties={
                  !isCreateMode && load
                    ? {
                        loadID: load.ID,
                        freightTrackingID: load.freightTrackingID,
                        serviceID: load.serviceID,
                      }
                    : undefined
                }
              >
                {isLoading ? <ButtonLoader /> : buttonText}
              </Button>

              {isCreateMode && formMethods.formState.isDirty && (
                <Flex direction='row' justify='center' align='center'>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    type='button'
                    className='w-50% mt-4 h-8 text-sm text-neutral-700'
                    disabled={isLoading}
                    variant='outline'
                    onClick={() => formMethods.reset()}
                  >
                    {ButtonText.ClearForm}
                  </Button>
                </Flex>
              )}
            </Flex>
          </form>
        </FormProvider>
      </ExtendedFormProvider>
    </Flex>
  );
}
